import React, { useState, useEffect, useMemo, Suspense } from 'react';
import apiClient from '../../../api/client';
import type { TargetUniverseData, TargetNode, PredictedTarget, UniverseNode } from '../../../types/targetUniverse';
import TargetUniverse2D from './target-universe/TargetUniverse2D';
import SelectivityRadar from './target-universe/SelectivityRadar';
import SeriesHeatmap from './target-universe/SeriesHeatmap';
import SimilarityCollision from './target-universe/SimilarityCollision';

// Lazy-load 3D Globe to avoid GPU memory contention with other components
const TargetUniverse3D = React.lazy(() => import('./target-universe/TargetUniverse3D'));

// Theme-aware colour helpers — read CSS variables at render time
function getC() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => s.getPropertyValue(name).trim() || fallback;
  return {
    bg: v('--content-bg', '#0d1117'),
    panel: v('--card-bg', '#161b22'),
    panelBorder: v('--card-border', '#1e293b'),
    text: v('--text-primary', '#e2e8f0'),
    textMuted: v('--text-secondary', '#64748b'),
    textDim: v('--text-muted', '#475569'),
    accent: '#3b82f6',
    accentGlow: 'rgba(59, 130, 246, 0.3)',
    projectTarget: '#22d3ee',
    safe: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    pathway: '#8b5cf6',
  };
}

type SubView = 'scatter' | 'globe' | 'radar' | 'heatmap' | 'collision';

interface TargetUniverseTabProps {
  smiles: string;
  moleculeId?: number;
}

const VIEWS: { id: SubView; label: string; icon: string; tooltip: string }[] = [
  { id: 'scatter', label: 'How Selective?', icon: '\u2299', tooltip: 'Compound selectivity across target panel' },
  { id: 'globe', label: 'Biological Network', icon: '\u25C9', tooltip: 'Target relationships in pathway context' },
  { id: 'radar', label: 'Safety Radar', icon: '\u25C7', tooltip: 'Off-target safety profile at a glance' },
  { id: 'heatmap', label: 'SAR Map', icon: '\u25A6', tooltip: 'Structure-activity across all targets' },
  { id: 'collision', label: 'Collision Map', icon: '\u2B21', tooltip: 'Similar compound overlap analysis' },
];

const TOP_N_OPTIONS = [10, 20, 50] as const;

// ─── Build UniverseNode[] from raw API data ───
function buildUniverseNodes(
  targets: TargetNode[],
  predictedTargets: PredictedTarget[],
): UniverseNode[] {
  const allNodes: UniverseNode[] = [];

  targets.forEach(t => {
    allNodes.push({
      id: t.chembl_id || t.target_name,
      name: t.target_name,
      fullName: t.target_name,
      uniprot: t.uniprot_id,
      isProjectTarget: t.is_primary,
      pIC50: t.pIC50 || 5,
      activityNM: t.activity_value || Math.pow(10, 9 - (t.pIC50 || 5)),
      known: true,
      pathways: t.pathways || [],
      safetyFlag: t.safety_flag,
      compoundCount: t.compound_count,
      x: 0, y: 0, z: 0,
    });
  });

  predictedTargets.forEach(t => {
    allNodes.push({
      id: t.chembl_id || t.target_name,
      name: t.target_name,
      fullName: t.target_name,
      uniprot: t.uniprot_id,
      isProjectTarget: false,
      pIC50: t.pIC50 || 5,
      activityNM: Math.pow(10, 9 - (t.pIC50 || 5)),
      known: false,
      pathways: [],
      compoundCount: 1,
      x: 0, y: 0, z: 0,
    });
  });

  return allNodes;
}

// ─────────────────────────────────────────────────────────────
// Target Detail Panel (inspector for non-globe views)
// ─────────────────────────────────────────────────────────────
const TargetDetail: React.FC<{
  target: UniverseNode | null;
  projectPIC50: number;
}> = ({ target, projectPIC50 }) => {
  const C = getC();
  if (!target) {
    return (
      <div style={{
        color: C.textDim, fontSize: 12,
        fontFamily: "'JetBrains Mono', monospace",
        textAlign: 'center', padding: '20px 12px',
      }}>
        Click a target node to inspect
      </div>
    );
  }

  const ratio = Math.pow(10, projectPIC50 - target.pIC50);
  const ratioColor = ratio > 100 ? C.safe : ratio > 10 ? C.warning : C.danger;
  const ratioLabel = ratio > 100 ? 'SAFE' : ratio > 10 ? 'MONITOR' : 'RISK';

  return (
    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: target.isProjectTarget ? C.projectTarget : C.text, marginBottom: 2 }}>
        {target.name}
      </div>
      {target.fullName && target.fullName !== target.name && (
        <div style={{ color: C.textMuted, fontSize: 10, marginBottom: 8 }}>{target.fullName}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
        <div style={{ padding: 6, background: 'rgba(59,130,246,0.08)', borderRadius: 6, border: '1px solid rgba(59,130,246,0.15)' }}>
          <div style={{ color: C.textDim, fontSize: 9, marginBottom: 2 }}>ACTIVITY</div>
          <div style={{ color: C.accent, fontSize: 14, fontWeight: 700 }}>
            {target.activityNM < 1000 ? `${Math.round(target.activityNM)} nM` : `${(target.activityNM / 1000).toFixed(1)} \u00B5M`}
          </div>
          <div style={{ color: C.textDim, fontSize: 9 }}>pIC50 = {target.pIC50.toFixed(1)}</div>
        </div>
        {!target.isProjectTarget && (
          <div style={{ padding: 6, background: `${ratioColor}10`, borderRadius: 6, border: `1px solid ${ratioColor}30` }}>
            <div style={{ color: C.textDim, fontSize: 9, marginBottom: 2 }}>SELECTIVITY RATIO</div>
            <div style={{ color: ratioColor, fontSize: 14, fontWeight: 700 }}>
              {ratio > 1000 ? `${(ratio / 1000).toFixed(0)}K\u00D7` : `${ratio.toFixed(0)}\u00D7`}
            </div>
            <div style={{ color: ratioColor, fontSize: 9, fontWeight: 600 }}>{ratioLabel}</div>
          </div>
        )}
      </div>

      {target.pathways.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ color: C.textDim, fontSize: 9, marginBottom: 4 }}>PATHWAYS</div>
          {target.pathways.map(pId => (
            <div key={pId} style={{ padding: '3px 6px', marginBottom: 2, background: 'rgba(139,92,246,0.08)', borderRadius: 4, border: '1px solid rgba(139,92,246,0.15)', color: C.pathway, fontSize: 9 }}>
              {pId}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
        {target.uniprot && (
          <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: 'rgba(100,116,139,0.15)', color: C.textMuted }}>
            {target.uniprot}
          </span>
        )}
        <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: target.known ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: target.known ? C.safe : C.danger }}>
          {target.known ? 'Exact match' : 'Predicted (\u226580%)'}
        </span>
        {target.safetyFlag && (
          <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: 'rgba(239,68,68,0.15)', color: C.danger }}>
            {'\u26A0'} {target.safetyFlag}
          </span>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN TAB COMPONENT
// ═══════════════════════════════════════════════════════════════
const TargetUniverseTab: React.FC<TargetUniverseTabProps> = ({ smiles, moleculeId }) => {
  const [data, setData] = useState<TargetUniverseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subView, setSubView] = useState<SubView>('globe');
  const [fetched, setFetched] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [topN, setTopN] = useState(20);

  // Follow system theme toggle — triggers re-render so getC() picks up new CSS vars
  const [, setThemeTick] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeTick(t => t + 1));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const C = getC();

  // ─── Data fetching ───
  useEffect(() => {
    if (!moleculeId || fetched) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`/v2/molecules/${moleculeId}/target-universe`);
        setData(res.data);
        setFetched(true);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to load target universe');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [moleculeId, fetched]);

  // ─── Build universe nodes ───
  const allNodes = useMemo(() => {
    if (!data) return [];
    return buildUniverseNodes(data.targets, data.predicted_targets);
  }, [data]);

  const projectPIC50 = useMemo(() =>
    allNodes.find(n => n.isProjectTarget)?.pIC50 || 7,
    [allNodes],
  );

  // ─── Filter to top N ───
  const filteredNodes = useMemo(() => {
    const projectNode = allNodes.find(n => n.isProjectTarget);
    const others = [...allNodes]
      .filter(n => !n.isProjectTarget)
      .sort((a, b) => b.pIC50 - a.pIC50)
      .slice(0, topN);
    return projectNode ? [projectNode, ...others] : others;
  }, [allNodes, topN]);

  // Filtered TargetNode[] for radar/heatmap — respects topN
  const filteredTargets = useMemo(() => {
    if (!data) return [];
    const filteredIds = new Set(filteredNodes.map(n => n.id));
    return data.targets.filter(t => filteredIds.has(t.chembl_id || t.target_name));
  }, [data, filteredNodes]);

  const selectedNode = useMemo(() =>
    allNodes.find(n => n.id === selectedTarget) || null,
    [allNodes, selectedTarget],
  );

  // ─── Loading state ───
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
        <div className="w-6 h-6 border-2 border-t-teal-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
        <span style={{ color: C.textMuted, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          Building target universe...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16, borderRadius: 8, backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
        <p style={{ color: '#ef4444', fontSize: 12 }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
        <span style={{ color: C.textMuted, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          No molecule selected
        </span>
      </div>
    );
  }

  if (data.targets.length === 0 && data.predicted_targets.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, color: C.textDim, fontFamily: "'JetBrains Mono', monospace" }}>
        <div style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }}>{'\uD83C\uDFAF'}</div>
        <div style={{ fontSize: 14, marginBottom: 8 }}>No target data available</div>
        <div style={{ fontSize: 12, color: C.textDim }}>Run Intelligence to discover targets via ChEMBL bioactivity data</div>
      </div>
    );
  }

  const risk = data.risk_summary;

  // Count risk categories
  const riskCounts = { risk: 0, watch: 0, safe: 0 };
  data.targets.forEach(t => {
    if (t.is_primary || !t.pIC50) return;
    const ratio = Math.pow(10, projectPIC50 - t.pIC50);
    if (ratio > 100) riskCounts.safe++;
    else if (ratio > 10) riskCounts.watch++;
    else riskCounts.risk++;
  });

  const showTopN = true; // Show Top N filter for all views

  return (
    <div style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
      {/* ═══ Context Bar ═══ */}
      <div style={{
        background: C.panel, border: `1px solid ${C.panelBorder}`, borderRadius: 6,
        padding: '6px 10px', marginBottom: 8, fontSize: 10,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 14 }}>{'\uD83C\uDFAF'}</span>
        {risk.primary_target && (
          <span style={{ color: C.projectTarget, fontWeight: 700 }}>{risk.primary_target}</span>
        )}
        <span style={{ color: C.textDim }}>|</span>
        <span style={{ color: C.text }}>
          {data.targets.length + data.predicted_targets.length} targets
        </span>
        <span style={{ color: C.textDim }}>|</span>
        {riskCounts.risk > 0 && <span style={{ color: C.danger }}>{riskCounts.risk} risk</span>}
        {riskCounts.watch > 0 && <span style={{ color: C.warning }}>{riskCounts.watch} watch</span>}
        {riskCounts.safe > 0 && <span style={{ color: C.safe }}>{riskCounts.safe} safe</span>}
      </div>

      {/* ═══ View Tabs + Top N ═══ */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.panelBorder}`, marginBottom: 8 }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setSubView(v.id)}
            title={v.tooltip}
            style={{
              padding: '6px 8px', background: 'none', border: 'none',
              borderBottom: subView === v.id ? `2px solid ${C.accent}` : '2px solid transparent',
              color: subView === v.id ? C.accent : C.textMuted,
              cursor: 'pointer', fontSize: 10, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 4,
              marginBottom: '-1px',
            }}>
            <span style={{ fontSize: 12 }}>{v.icon}</span> {v.label}
          </button>
        ))}

        {/* Top N filter — only for scatter & globe */}
        {showTopN && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, paddingRight: 4 }}>
            <span style={{ color: C.textDim, fontSize: 9 }}>
              {filteredNodes.length} of {allNodes.length}
            </span>
            {TOP_N_OPTIONS.map(n => (
              <button key={n} onClick={() => setTopN(n)}
                style={{
                  padding: '2px 8px', fontSize: 10, fontFamily: 'inherit',
                  background: topN === n ? 'rgba(59,130,246,0.2)' : C.panelBorder + '30',
                  border: `1px solid ${topN === n ? 'rgba(59,130,246,0.4)' : C.panelBorder}`,
                  borderRadius: 4,
                  color: topN === n ? C.accent : C.textMuted,
                  cursor: 'pointer',
                }}>
                Top {n}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ═══ View Content ═══ */}
      <div style={{ minHeight: 280 }}>
        {subView === 'globe' && (
          <Suspense fallback={
            <div className="flex items-center justify-center" style={{ minHeight: 280 }}>
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading 3D Globe...</span>
              </div>
            </div>
          }>
            <TargetUniverse3D
              nodes={filteredNodes}
              selectedTarget={selectedTarget}
              onSelectTarget={setSelectedTarget}
              projectPIC50={projectPIC50}
            />
          </Suspense>
        )}
        {subView === 'scatter' && (
          <TargetUniverse2D
            nodes={filteredNodes}
            selectedTarget={selectedTarget}
            onSelectTarget={setSelectedTarget}
            projectPIC50={projectPIC50}
          />
        )}
        {subView === 'radar' && (
          <SelectivityRadar targets={filteredTargets} />
        )}
        {subView === 'heatmap' && (
          <SeriesHeatmap
            seriesData={data.series_data}
            currentMoleculeId={data.molecule_id}
            targets={filteredTargets}
          />
        )}
        {subView === 'collision' && (
          <SimilarityCollision
            similarCompounds={data.similar_compounds}
            projectTarget={risk.primary_target}
          />
        )}
      </div>

      {/* ═══ Target Inspector (hidden when 3D globe is active — it has its own sidebar) ═══ */}
      {subView !== 'globe' && (
        <div style={{
          marginTop: 8, padding: 10,
          background: C.panel, border: `1px solid ${C.panelBorder}`, borderRadius: 6,
        }}>
          <div style={{ fontSize: 9, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Target Inspector
          </div>
          <TargetDetail target={selectedNode} projectPIC50={projectPIC50} />
        </div>
      )}

      {/* ═══ Risk Summary ═══ */}
      {risk.primary_target && (
        <div style={{
          marginTop: 8, padding: 10,
          background: C.panel, border: `1px solid ${C.panelBorder}`, borderRadius: 6,
        }}>
          <div style={{ fontSize: 9, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Risk Summary
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {(() => {
              const pIC50ToDisplay = (pic50: number | undefined) => {
                if (pic50 == null) return '\u2014';
                const nM = Math.pow(10, 9 - pic50);
                return nM < 1000 ? `${Math.round(nM)} nM` : `${(nM / 1000).toFixed(1)} \u00B5M`;
              };

              const selRatio = risk.selectivity_window != null ? Math.pow(10, risk.selectivity_window) : null;
              const selColor = risk.selectivity_window != null
                ? (risk.selectivity_window >= 2 ? C.safe : risk.selectivity_window >= 1 ? C.warning : C.danger)
                : C.textMuted;

              return [
                { label: 'On-target', value: pIC50ToDisplay(risk.primary_pIC50), sub: risk.primary_pIC50 != null ? `pIC50 ${risk.primary_pIC50.toFixed(1)}` : '', color: C.projectTarget },
                { label: 'Worst off-target', value: pIC50ToDisplay(risk.worst_offtarget_pIC50), sub: risk.worst_offtarget || '', color: C.warning },
                {
                  label: 'Selectivity',
                  value: selRatio != null ? (selRatio > 1000 ? `${(selRatio / 1000).toFixed(0)}K\u00D7` : `${selRatio.toFixed(0)}\u00D7`) : '\u2014',
                  sub: risk.selectivity_window != null ? `${risk.selectivity_window > 0 ? '+' : ''}${risk.selectivity_window.toFixed(1)} log` : '',
                  color: selColor,
                },
              ].map((m, i) => (
                <div key={i} style={{ padding: 6, background: `${m.color}08`, border: `1px solid ${m.color}25`, borderRadius: 6, textAlign: 'center' }}>
                  <div style={{ color: C.textDim, fontSize: 8, marginBottom: 2 }}>{m.label}</div>
                  <div style={{ color: m.color, fontSize: 14, fontWeight: 700 }}>{m.value}</div>
                  <div style={{ color: C.textDim, fontSize: 8, marginTop: 2 }}>{m.sub}</div>
                </div>
              ));
            })()}
          </div>

          {/* Safety Flags */}
          {risk.safety_flags.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {risk.safety_flags.map((flag, i) => {
                const isHerg = flag.toLowerCase().includes('herg');
                const isCYP = flag.toLowerCase().includes('cyp');
                const flagColor = isHerg || isCYP ? C.warning : C.danger;
                return (
                  <div key={i} style={{
                    padding: '3px 6px', fontSize: 9, borderRadius: 4,
                    background: `${flagColor}12`, border: `1px solid ${flagColor}30`,
                    color: flagColor, display: 'flex', alignItems: 'center', gap: 3,
                  }}>
                    <span style={{ fontSize: 10 }}>{'\u26A0'}</span> {flag}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TargetUniverseTab;
